import React, { useEffect, useState } from "react";
import { Box, Button, LinearProgress, MenuItem } from "@mui/material";
import Menu from "@mui/material/Menu";
import { Add } from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { courseService } from "../../../../../../services/course.service";
import useConfirmationDialog from "../../../../../../hooks/useConfirmationDialog";
import ContentWrapper from "../../../../../../shared/ContentWrapper";
import ChapterNameDrawer from "./drawers/ChapterNameDrawer";
import ChapterResultDrawer from "./drawers/ChapterResultDrawer";
import ChapterLectureDrawer from "./drawers/ChapterLectureDrawer";
import ChapterTestDrawer from "./drawers/ChapterTestDrawer";
import ChapterTaskDrawer from "./drawers/ChapterTestDrawer/ChapterTaskDrawer";
import { trash } from "../../../../../../icon/trash";
import { edit } from "../../../../../../icon/edit";
import { useNavigate } from "react-router-dom";

interface Chapter {
  id: string;
  name: string;
  description: string;
  items: any[];
  content: any[];
  result: string;
}

const useFetchCourseParts = (courseId: string) => {
  return useQuery({
    queryKey: ["courseParts", courseId],
    queryFn: () => courseService.getCourseParts(courseId),
  });
};

type ActiveDrawer = "name" | "result" | "lecture" | "test" | "task" | null;

const ConstructorStepTwo = ({ courseId, setStep }: any) => {
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>();
  const [selectedContent, setSelectedContent] = useState();
  const [selectedChapterIndex, setSelectedChapterIndex] = useState<number>(-1);
  const [selectedContentIndex, setSelectedContentIndex] = useState<number>(-1);
  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(null);
  const [courseChapters, setCourseChapters] = useState<Chapter[]>([]);
  const { showDialog, ConfirmationDialog } = useConfirmationDialog();
  const queryClient = useQueryClient();
  const {
    data: courseParts,
    isLoading,
    isError,
  } = useFetchCourseParts(courseId);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(
    null
  );

  const navigate = useNavigate();

  const confirmDelete = (
    type: string,
    item: string,
    onDeleteConfirmed: () => Promise<void>
  ) => {
    showDialog(
      type,
      item,
      () => onDeleteConfirmed(),
      () => console.log("Unsubscribed from deleting")
    );
  };

  const addChapter = async () => {
    const newChapterData = {
      name: "Название раздела",
      description: "",
      course_id: courseId,
    };
    try {
      const response = await courseService.createCoursePart(
        courseId,
        newChapterData
      );
      setCourseChapters((prevChapters) => [...prevChapters, response.data]);
    } catch (error) {
      console.error("Ошибка при создании раздела:", error);
    }
  };
  const openDrawer = (drawer: ActiveDrawer) => {
    setActiveDrawer(drawer);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };
  useEffect(() => {
    if (courseParts) {
      setCourseChapters(courseParts.data);
    }
  }, [courseParts]);

  const handleDeleteChapter = async (
    chapterId: string,
    chapterName: string
  ) => {
    confirmDelete("", chapterName, async () => {
      try {
        await courseService.deleteCoursePart(chapterId);
        queryClient.invalidateQueries({ queryKey: ["courseParts", courseId] });
      } catch (error) {
        console.error("Ошибка при удалении раздела:", error);
      }
    });
  };

  const editChapterName = (chapter: Chapter, index: number) => {
    setSelectedChapter(chapter);
    setSelectedChapterIndex(index);
    openDrawer("name");
  };

  const handleSaveLecture = (lectureData: any) => {
    if (!selectedChapter) {
      console.error("Раздел не выбран");
      return;
    }
    const updatedItems =
      selectedContentIndex >= 0
        ? courseChapters[selectedChapterIndex].items.map((item, idx) =>
            idx === selectedContentIndex ? lectureData : item
          )
        : [...courseChapters[selectedChapterIndex].items, lectureData];

    const updatedChapters = courseChapters.map((chapter, idx) => {
      if (idx === selectedChapterIndex) {
        return { ...chapter, items: updatedItems };
      }
      return chapter;
    });

    setCourseChapters(updatedChapters);
    handleSaveAll(updatedChapters[selectedChapterIndex]);
  };

  const handleSaveTest = (testData: any) => {
    if (!selectedChapter) {
      console.error("Раздел не выбран");
      return;
    }

    const updatedItems =
      selectedContentIndex >= 0
        ? courseChapters[selectedChapterIndex].items.map((item, idx) =>
            idx === selectedContentIndex ? testData : item
          )
        : [...courseChapters[selectedChapterIndex].items, testData];

    const updatedChapters = courseChapters.map((chapter, idx) => {
      if (idx === selectedChapterIndex) {
        return { ...chapter, items: updatedItems };
      }
      return chapter;
    });

    setCourseChapters(updatedChapters);

    setCourseChapters(updatedChapters);
    handleSaveAll(updatedChapters[selectedChapterIndex]);
  };

  const handleSaveTask = (taskData: any) => {
    if (!selectedChapter) {
      console.error("Раздел не выбран");
      return;
    }

    const updatedItems =
      selectedContentIndex >= 0
        ? courseChapters[selectedChapterIndex].items.map((item, idx) =>
            idx === selectedContentIndex ? taskData : item
          )
        : [...courseChapters[selectedChapterIndex].items, taskData];

    const updatedChapters = courseChapters.map((chapter, idx) => {
      if (idx === selectedChapterIndex) {
        return { ...chapter, items: updatedItems };
      }
      return chapter;
    });

    setCourseChapters(updatedChapters);

    setCourseChapters(updatedChapters);
    handleSaveAll(updatedChapters[selectedChapterIndex]);
  };

  const handleSaveAll = async (updatedChapter: Chapter) => {
    try {
      await courseService.createCourseContent(
        updatedChapter.id,
        updatedChapter.items
      );
    } catch (error) {
      console.error("Ошибка при создании контента:", error);
    }
  };

  const updateChapterName = async (chapterId: string, newName: string) => {
    try {
      const currentChapter = courseChapters.find(
        (chapter) => chapter.id === chapterId
      );
      const updatedData = {
        name: newName,
        description: currentChapter?.description || "",
      };
      await courseService.updateCoursePart(chapterId, updatedData);

      const updatedChapters = courseChapters.map((chapter) =>
        chapter.id === chapterId ? { ...chapter, name: newName } : chapter
      );
      setCourseChapters(updatedChapters);
    } catch (error) {
      console.error("Ошибка при обновлении названия раздела:", error);
    }
  };
  const updateResultDrawer = async (
    chapterId: string,
    newDescription: string
  ) => {
    try {
      const currentChapter = courseChapters.find(
        (chapter) => chapter.id === chapterId
      );
      const updatedData = {
        name: currentChapter?.name || "",
        description: newDescription,
      };
      await courseService.updateCoursePart(chapterId, updatedData);

      const updatedChapters = courseChapters.map((chapter) =>
        chapter.id === chapterId
          ? { ...chapter, description: newDescription }
          : chapter
      );
      setCourseChapters(updatedChapters);
    } catch (error) {
      console.error("Ошибка обновления описания раздела:", error);
    }
  };

  const editChapterResult = (chapterId: string) => {
    setSelectedChapterId(chapterId);
    const chapter = courseChapters.find((chapter) => chapter.id === chapterId);
    if (chapter) {
      setSelectedChapter(chapter);
    }
    setActiveDrawer("result");
  };
  const addChapterResult = (chapterId: string) => {
    setSelectedChapterId(chapterId);
    const chapter = courseChapters.find((chapter) => chapter.id === chapterId);
    if (chapter) {
      setSelectedChapter(chapter);
    }
    setActiveDrawer("result");
  };

  const [addContentMenuAnchorEl, setAddContentMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const openContentAddMenu = Boolean(addContentMenuAnchorEl);

  const handleClickContentMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    chapterId: string
  ) => {
    setAddContentMenuAnchorEl(event.currentTarget);
    setSelectedChapterId(chapterId);
  };

  const handleCloseContentMenu = () => {
    setAddContentMenuAnchorEl(null);
  };
  const handleContentTypeSelect = (
    val: string,
    chapterId: string,
    contentIndex: number,
    content?: any
  ) => {
    if (!selectedChapterId) {
      setSelectedChapterId(chapterId);
    }

    handleCloseContentMenu();
    const chapterIndex = courseChapters.findIndex(
      (chapter) => chapter.id === selectedChapterId
    );
    if (chapterIndex !== -1) {
      setSelectedChapter(courseChapters[chapterIndex]);
      setSelectedChapterIndex(chapterIndex);
    }

    setSelectedContent(content);
    setSelectedContentIndex(contentIndex);

    if (val === "lecture") {
      setActiveDrawer("lecture");
    } else if (val === "test") {
      setActiveDrawer("test");
    } else if (val === "task") {
      setActiveDrawer("task");
    } else {
      console.error("Неизвестный тип контента:", val);
      return;
    }
  };

  const handleDeleteContent = (chapterIndex: number, contentIndex: number) => {
    const contentType = courseChapters[chapterIndex].items[contentIndex]
      .lecture_name
      ? "лекции"
      : courseChapters[chapterIndex].items[contentIndex].test_name
      ? "теста"
      : courseChapters[chapterIndex].items[contentIndex].exercise_name
      ? "задания"
      : "Неизвестный контент";

    const contentName =
      courseChapters[chapterIndex].items[contentIndex].lecture_name ||
      courseChapters[chapterIndex].items[contentIndex].test_name ||
      courseChapters[chapterIndex].items[contentIndex].exercise_name ||
      "";

    confirmDelete(contentType, contentName, async () => {
      try {
        const updatedItems = courseChapters[chapterIndex].items.filter(
          (item, idx) => idx !== contentIndex
        );
        const updatedChapters = courseChapters.map((chapter, idx) => {
          if (idx === chapterIndex) {
            return { ...chapter, items: updatedItems };
          }
          return chapter;
        });
        setCourseChapters(updatedChapters);
        handleSaveAll(updatedChapters[chapterIndex]);
      } catch (error) {
        console.error("Ошибка при удалении контента:", error);
      }
    });
  };

  if (isLoading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }

  if (isError) {
    return <div>Ошибка получения курса! </div>;
  }
  return (
    <>
      <ContentWrapper>
        <ConfirmationDialog />
        <div className="course-content">
          <div className="constructor-content-title">
            Добавьте учебный план вашего курса
          </div>
          <div className="corse-content-description">
            Начните составлять свой курс, создав разделы, лекции и практические
            задания (тесты, задания). Используйте план курса для выстраивания
            структуры материалов и понятной маркировки разделов и лекций.
          </div>

          {Array.isArray(courseChapters) &&
            courseChapters.map((chapter, chapterIndex) => (
              <div className="chapter-wrapper" key={chapter.id}>
                <div className="chapter-name">
                  Раздел {chapterIndex + 1}: {`${chapter.name} `}
                  <span onClick={() => editChapterName(chapter, chapterIndex)}>
                    {edit}
                  </span>
                  {chapterIndex > 0 && (
                    <span
                      onClick={() =>
                        handleDeleteChapter(chapter.id, chapter.name)
                      }>
                      {trash}
                    </span>
                  )}
                </div>
                {chapter?.description?.length > 0 && (
                  <div className="chapter-result">
                    <span> Результат раздела:&nbsp; </span>
                    {chapter.description.length > 40
                      ? `${chapter.description.substring(0, 40)}...`
                      : chapter.description}
                    <span onClick={() => editChapterResult(chapter.id)}>
                      {" "}
                      {edit}
                    </span>
                  </div>
                )}

                <div className="chapter-controls">
                  {!chapter.description?.length && (
                    <Button
                      sx={{
                        color: "#0573E4",
                        fontSize: "14px",
                        fontWeight: "600",
                        marginTop: "12px",
                        textTransform: "none",
                      }}
                      className="add-field"
                      onClick={() => addChapterResult(chapter.id)}
                      variant="text">
                      <Add />
                      Добавить результат раздела
                    </Button>
                  )}

                  {chapter.items &&
                    chapter.items.length > 0 &&
                    chapter.items.map((content, contentIndex) => {
                      let contentTypeLabel, additionalInfo;
                      if (content.test_name) {
                        contentTypeLabel = "Тест";
                        additionalInfo = `${content.questions.length} вопросов`;
                      } else if (content.lecture_name) {
                        contentTypeLabel = "Лекция";
                        additionalInfo = content.article
                          ? "Статья"
                          : content.video
                          ? content.video_name + content.video_extension
                          : content.slides
                          ? content.slides_file_name +
                            content.slides_file_extension
                          : "";
                      } else if (content.exercise_name) {
                        contentTypeLabel = "Задание";
                      }

                      return (
                        <div className="chapter-result" key={contentIndex}>
                          <span>
                            {contentTypeLabel} «
                            {content.lecture_name ||
                              content.test_name ||
                              content.exercise_name}
                            »
                          </span>
                          {additionalInfo && <div> &nbsp;{additionalInfo}</div>}

                          <span
                            className="edit"
                            onClick={() =>
                              handleContentTypeSelect(
                                content.lecture_name
                                  ? "lecture"
                                  : content.test_name
                                  ? "test"
                                  : "task",
                                chapter.id,
                                contentIndex,
                                content
                              )
                            }>
                            {edit}
                          </span>
                          <span
                            className="remove"
                            onClick={() =>
                              handleDeleteContent(chapterIndex, contentIndex)
                            }>
                            {trash}
                          </span>
                        </div>
                      );
                    })}
                  <Button
                    sx={{
                      color: "#0573E4",
                      fontSize: "14px",
                      fontWeight: "600",
                      marginTop: "12px",
                      textTransform: "none",
                    }}
                    className="add-field"
                    aria-controls={
                      openContentAddMenu ? "basic-menu" : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={openContentAddMenu ? "true" : undefined}
                    onClick={(e) => handleClickContentMenu(e, chapter.id)}
                    variant="text">
                    <Add />
                    Добавить контент
                  </Button>
                  <Menu
                    id="basic-menu"
                    anchorEl={addContentMenuAnchorEl}
                    open={openContentAddMenu}
                    onClose={handleCloseContentMenu}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}>
                    <MenuItem
                      onClick={() =>
                        handleContentTypeSelect("lecture", chapter.id, -1)
                      }>
                      Лекция
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleContentTypeSelect("test", chapter.id, -1)
                      }>
                      Тест
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleContentTypeSelect("task", chapter.id, -1)
                      }>
                      Задание
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            ))}

          <Button
            sx={{
              color: "#0573E4",
              fontSize: "16px",
              fontWeight: "600",
              textTransform: "none",
            }}
            className="add-field"
            onClick={() => {
              addChapter();
            }}
            variant="text">
            <Add />
            Добавить раздел
          </Button>
        </div>
      </ContentWrapper>
      {activeDrawer === "name" && selectedChapter && (
        <ChapterNameDrawer
          name={selectedChapter?.name || ""}
          open={true}
          close={closeDrawer}
          updateChapterName={updateChapterName}
          selectedChapterId={selectedChapter?.id || ""}
        />
      )}
      {activeDrawer === "result" && selectedChapter && (
        <ChapterResultDrawer
          result={selectedChapter?.description || ""}
          open={true}
          close={closeDrawer}
          updateResultDrawer={updateResultDrawer}
          selectedChapterId={selectedChapter?.id || ""}
        />
      )}
      {activeDrawer === "lecture" && selectedChapter && (
        <ChapterLectureDrawer
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
          onSaveLecture={handleSaveLecture}
          lecture={selectedContent}
          contentIndex={selectedContentIndex}
          selectedChapterId={selectedChapter.id}
          open={true}
          close={closeDrawer}
        />
      )}
      {activeDrawer === "test" && selectedChapter && (
        <ChapterTestDrawer
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
          onSaveTest={handleSaveTest}
          test={selectedContent}
          contentIndex={selectedContentIndex}
          selectedChapterId={selectedChapter.id}
          open={true}
          close={closeDrawer}
        />
      )}

      {activeDrawer === "task" && selectedChapter && (
        <ChapterTaskDrawer
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
          onSaveTask={handleSaveTask}
          task={selectedContent}
          contentIndex={selectedContentIndex}
          selectedChapterId={selectedChapter.id}
          open={true}
          close={closeDrawer}
        />
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#F5F8FB",
          border: "1px solid #E5E5E5",
          padding: "12px 32px 12px 32px",
          marginTop: "52px",
          "@media (max-width:1000px)": {
            backgroundColor: "transparent",
            border: "none",
            width: "100%",
            padding: 0,
          },
        }}>
        <Box>
          <Button
            sx={{ marginLeft: "12px" }}
            variant="outlined"
            onClick={() => setStep(1)}>
            Назад
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: "12px", marginLeft: "auto" }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => navigate("/panel")}>
            Сохранить как черновик
          </Button>

          <Button type="submit" variant="contained" onClick={() => setStep(3)}>
            Далее
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ConstructorStepTwo;
